import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Semester, SemesterRequest } from "../../models/semester";
import { Subject, SubjectRequest } from "../../models/subject";
import { GradeFile } from "../../models/grade-file";
import subjectEquals from "../../utils/subjectCompare";
import { LOCAL_GRADE_FILE_KEY } from "../../constant/constant";
import i18n from "../../translation/i18n";

export interface SubjectFormData {
  visible: boolean;
  mode: "edit" | "create";
  subject: SubjectRequest | null | undefined;
  semesterId: number;
}

export interface SemesterFormData {
  visible: boolean;
  mode: "edit" | "create";
  semester: SemesterRequest | null | undefined;
}

interface GradeFileSlideState {
  gradeFile: GradeFile;
  subjectFormData: SubjectFormData;
  semesterFormData: SemesterFormData;
}

const processSampleLocalFile = () => {
  const sampleLocalFile: GradeFile = {
    semesters: [
      {
        id: 1,
        name: i18n.t("gpa_cpa.sample_file.semester_name"),
        subjects: [
          {
            id: 1,
            name: i18n.t("gpa_cpa.sample_file.subject_name"),
            mark: 3,
            credit: 2,
            targetMark: 4,
          },
        ],
      },
    ],
    target: 3.2,
  };
  return sampleLocalFile;
};

const initialState: GradeFileSlideState = {
  gradeFile: processSampleLocalFile(),
  subjectFormData: {
    visible: false,
    mode: "edit",
    subject: {
      name: "",
      credit: 0,
    },
    semesterId: 0,
  },
  semesterFormData: {
    visible: false,
    mode: "edit",
    semester: {
      name: "",
    },
  },
};

const saveGradeFile = (
  gradeFileSliceState: GradeFileSlideState,
  newSemesterState: Semester[]
) => {
  const newGradeFile: GradeFile = {
    ...gradeFileSliceState.gradeFile,
    semesters: newSemesterState,
    lastModifyAt: new Date(),
  };
  localStorage.setItem(LOCAL_GRADE_FILE_KEY, JSON.stringify(newGradeFile));
};

export const gradeFileSlide = createSlice({
  name: "gradeFile",
  initialState,
  reducers: {
    loadLocalGradeFile: (state) => {
      const localFileStored = localStorage.getItem(LOCAL_GRADE_FILE_KEY);
      let localFile: GradeFile;
      if (localFileStored) {
        localFile = JSON.parse(localFileStored);
      } else {
        localFile = processSampleLocalFile();
      }
      state.gradeFile = localFile;
    },
    updateSemesterStateSubjectScreen: (
      state,
      action: PayloadAction<{
        subject: Subject;
        semesterId: number;
      }>
    ) => {
      const { semesterId, subject } = action.payload;
      state.gradeFile.semesters = state.gradeFile.semesters.map((semester) => {
        if (semester.id === semesterId) {
          return {
            ...semester,
            subjects: semester.subjects.map((s) => {
              if (subjectEquals(s, subject)) {
                return {
                  ...subject,
                };
              }
              return s;
            }),
          };
        }
        return semester;
      });
      saveGradeFile(state, state.gradeFile.semesters);
    },
    setGradeTarget: (
      state,
      action: PayloadAction<{
        target: number | "";
        semesters: Semester[];
      }>
    ) => {
      if (state.gradeFile) {
        if (!action.payload.target) {
          delete state.gradeFile.target;
        } else {
          state.gradeFile.target = action.payload.target;
        }
      }
      state.gradeFile.semesters = action.payload.semesters;
      saveGradeFile(state, state.gradeFile.semesters);
    },
    showSubjectFormData: (
      state,
      action: PayloadAction<{
        mode: "edit" | "create";
        subject: Subject | null | undefined;
        semesterId: number;
      }>
    ) => {
      state.subjectFormData = {
        ...action.payload,
        visible: true,
      };
    },
    hideSubjectFormData: (state) => {
      state.subjectFormData.visible = false;
    },
    showSemesterFormData: (
      state,
      action: PayloadAction<{
        mode: "edit" | "create";
        semester: SemesterRequest | null | undefined;
      }>
    ) => {
      state.semesterFormData = {
        ...action.payload,
        visible: true,
      };
    },
    hideSemesterFormData: (state) => {
      state.semesterFormData.visible = false;
    },
    resetGradeFile: (state) => {
      state.gradeFile = processSampleLocalFile();
      saveGradeFile(state, state.gradeFile.semesters);
    },
    updateSemesterStateSubject: (
      state,
      action: PayloadAction<{
        subject: Subject;
        semesterId: number;
      }>
    ) => {
      const newSemesterState = state.gradeFile.semesters.map((semester) => {
        if (semester.id === action.payload.semesterId) {
          return {
            ...semester,
            subjects: semester.subjects.map((s) => {
              if (subjectEquals(s, action.payload.subject)) {
                return {
                  ...action.payload.subject,
                };
              }
              return s;
            }),
          };
        }
        return semester;
      });
      const newFileInfo: GradeFile = {
        ...state.gradeFile,
        semesters: newSemesterState,
      };
      saveGradeFile(state, newSemesterState);
      state.gradeFile = newFileInfo;
      state.subjectFormData.visible = false;
    },
    deleteSemesterStateSubject: (
      state,
      action: PayloadAction<{
        subject: Subject;
        semesterId: number;
      }>
    ) => {
      const newSemesterState = state.gradeFile.semesters.map((semester) => {
        if (semester.id === action.payload.semesterId) {
          return {
            ...semester,
            subjects: semester.subjects.filter(
              (s) => !subjectEquals(s, action.payload.subject)
            ),
          };
        }
        return semester;
      });
      const newFileInfo: GradeFile = {
        ...state.gradeFile,
        semesters: newSemesterState,
      };
      saveGradeFile(state, newSemesterState);
      state.gradeFile = newFileInfo;
      state.subjectFormData.visible = false;
    },
    addSemesterStateSubject: (
      state,
      action: PayloadAction<{
        subject: Subject;
        semesterId: number;
      }>
    ) => {
      const newSemesterState = state.gradeFile.semesters.map((semester) => {
        if (semester.id === action.payload.semesterId) {
          return {
            ...semester,
            subjects: [
              ...semester.subjects,
              {
                ...action.payload.subject,
                id:
                  semester.subjects.length === 0
                    ? 1
                    : semester.subjects[semester.subjects.length - 1].id + 1,
              },
            ],
          };
        }
        return semester;
      });
      const newFileInfo: GradeFile = {
        ...state.gradeFile,
        semesters: newSemesterState,
      };
      saveGradeFile(state, newSemesterState);
      state.gradeFile = newFileInfo;
      state.subjectFormData.visible = false;
    },
    updateSemesterStateSemester: (
      state,
      action: PayloadAction<SemesterRequest>
    ) => {
      const newSemesterState = state.gradeFile.semesters.map((semester) => {
        if (semester.id === action.payload.id) {
          return {
            ...semester,
            ...action.payload,
          };
        }
        return semester;
      });
      const newFileInfo: GradeFile = {
        ...state.gradeFile,
        semesters: newSemesterState,
      };
      saveGradeFile(state, newSemesterState);
      state.gradeFile = newFileInfo;
      state.semesterFormData.visible = false;
    },
    deleteSemesterStateSemester: (state, action: PayloadAction<number>) => {
      const newSemesterState = state.gradeFile.semesters.filter(
        (semester) => semester.id !== action.payload
      );
      const newFileInfo: GradeFile = {
        ...state.gradeFile,
        semesters: newSemesterState,
      };
      saveGradeFile(state, newSemesterState);
      state.gradeFile = newFileInfo;
      state.semesterFormData.visible = false;
    },
    addSemesterStateSemester: (
      state,
      action: PayloadAction<SemesterRequest>
    ) => {
      const maxSemesterId =
        state.gradeFile.semesters.length === 0
          ? 0
          : Math.max(
              ...state.gradeFile.semesters.map((semester) => semester.id)
            );
      const newSemesterState: Semester[] = [
        ...state.gradeFile.semesters,
        {
          id: maxSemesterId + 1,
          ...action.payload,
          subjects: [],
        },
      ];
      const newFileInfo: GradeFile = {
        ...state.gradeFile,
        semesters: newSemesterState,
      };
      saveGradeFile(state, newSemesterState);
      state.gradeFile = newFileInfo;
      state.semesterFormData.visible = false;
    },
  },
});

export const {
  loadLocalGradeFile,
  setGradeTarget,
  showSubjectFormData,
  hideSubjectFormData,
  updateSemesterStateSubjectScreen,
  showSemesterFormData,
  hideSemesterFormData,
  resetGradeFile,
  updateSemesterStateSubject,
  deleteSemesterStateSubject,
  addSemesterStateSubject,
  updateSemesterStateSemester,
  deleteSemesterStateSemester,
  addSemesterStateSemester,
} = gradeFileSlide.actions;
